import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  LinearProgress,
  Card,
  CardContent,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  Snackbar,
  CircularProgress,
  Stack
} from '@mui/material';
import {
  DataGrid
} from '@mui/x-data-grid';
import {
  Phone as PhoneIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  RateReview as ReviewIcon,
  FilterList as FilterIcon,
  Transcribe as TranscribeIcon,
  Psychology as AnalyzeIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  PlayCircle as PlayCircleIcon,
  Stop as StopIcon,
  Settings as SettingsIcon,
  BatchPrediction as BulkIcon,
  Send as SendIcon,
  Star as StarIcon,
  FindInPage as FindRecordingIcon,
  ForwardToInbox as ForwardIcon,
  Assignment as AssignmentIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  AssignmentLate as AssignmentLateIcon,
  AssignmentReturn as AssignmentReturnIcon,
  PlayArrow as PlayArrowIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import {
  getCalls,
  getCall,
  startTranscription,
  getTranscriptionStatus,
  startAnalysis,
  getTranscriptionProviders,
  getProviderPricing,
  bulkProcessCalls,
  getCallRecording,
  updateQAScore,
  getAIPrompts,
  getAIModels,
  updateRecordingPath,
  getCallLeadFlow,
  moveLeadToQA
} from '../services/aiService';
import { submitQAEvaluation, getCallForAnalysis, getQAScoreAuditTrail, getLeadsByCallId, updateLead } from '../services/qaService';
import { getFieldMappings } from '../services/fieldMappingService';
import LeadSubmission from '../components/LeadSubmission';
import LeadFlow from '../components/LeadFlow';
import AudioPlayer from '../components/AudioPlayer';
import { useAuth } from '../contexts/AuthContext';

const QA_CRITERIA = {
  communication: { name: 'Communication Skills', maxScore: 20 },
  knowledge: { name: 'Product/Service Knowledge', maxScore: 20 },
  process: { name: 'Process Adherence', maxScore: 25 },
  customerService: { name: 'Customer Service', maxScore: 20 },
  compliance: { name: 'Compliance & Legal', maxScore: 15 }
};

const Calls = () => {
  const { user } = useAuth();
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCalls, setTotalCalls] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [selectedCalls, setSelectedCalls] = useState([]);
  
  // Column visibility state
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({
    lead_status: true,  // Ensure lead_status is always visible
  });
  
  // Filter states
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    transcription_status: '',
    analysis_status: '',
    start_date: '',
    end_date: '',
    agent_id: ''
  });

  // Agent data for filtering
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);

  // Dialog states
  const [transcriptionDialog, setTranscriptionDialog] = useState({ open: false, call: null });
  const [analysisDialog, setAnalysisDialog] = useState({ open: false, call: null });
  const [callDetailDialog, setCallDetailDialog] = useState({ open: false, call: null });
  const [bulkProcessDialog, setBulkProcessDialog] = useState({ open: false });
  const [settingsDialog, setSettingsDialog] = useState({ open: false });
  const [leadSubmissionDialog, setLeadSubmissionDialog] = useState({ open: false });
  const [qaDialog, setQADialog] = useState({ open: false, call: null });
  // Simplified QA scoring - single categorical rating
  const [qaScore, setQAScore] = useState('');
  const [qaComments, setQAComments] = useState('');

  // Provider and configuration states
  const [providers, setProviders] = useState({});
  const [aiModels, setAiModels] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [transcriptionConfig, setTranscriptionConfig] = useState({
    provider: 'openai',
    model: 'whisper-1',
    model_id: ''
  });
  const [analysisConfig, setAnalysisConfig] = useState({
    provider: 'openai',
    model: 'gpt-4',
    prompt_id: '',
    model_id: ''
  });

  // Processing states
  const [processingCalls, setProcessingCalls] = useState(new Set());
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Enhanced audio player state
  const [audioPlayer, setAudioPlayer] = useState({ 
    playing: false, 
    callId: null, 
    audioUrl: null,
    loading: false,
    isDialogMode: false  // Track if audio should play in dialog vs main table
  });
  
  // Audio element ref for main table playback
  const audioRef = useRef(null);

  // Lead information state
  const [leadInfo, setLeadInfo] = useState({
    data: null,
    loading: false,
    error: null,
    editing: false,
    editData: null
  });

  // Field mappings state
  const [fieldMappings, setFieldMappings] = useState({});

  // Permission helpers
  const canTranscribe = () => {
    return user && ['qa-manager', 'admin', 'superadmin'].includes(user.role);
  };

  const canAnalyze = () => {
    return user && ['qa-manager', 'admin', 'superadmin'].includes(user.role);
  };

  const canUpdateQA = () => {
    return user && ['qa-user', 'qa-manager', 'admin', 'superadmin'].includes(user.role);
  };

  const canBulkProcess = () => {
    return user && ['admin', 'superadmin'].includes(user.role);
  };

  const getScoreBadgeColor = (score, scoreValue = null) => {
    // Handle categorical scores
    if (scoreValue && typeof scoreValue === 'string') {
      switch (scoreValue.toLowerCase()) {
        case 'good':
          return { backgroundColor: '#2196F3', color: 'white' }; // Blue
        case 'average':
          return { backgroundColor: '#FFD700', color: 'black' }; // Yellow
        case 'poor':
          return { backgroundColor: '#9E9E9E', color: 'white' }; // Grey
        case 'rejected':
          return { backgroundColor: '#F44336', color: 'white' }; // Red
        default:
          return { backgroundColor: '#f5f5f5', color: '#666' }; // Default grey
      }
    }
    
    // Handle numerical scores - convert to categorical colors
    if (score >= 80) return { backgroundColor: '#2196F3', color: 'white' }; // Blue (Good)
    if (score >= 60) return { backgroundColor: '#FFD700', color: 'black' }; // Yellow (Average)
    return { backgroundColor: '#F44336', color: 'white' }; // Red (Rejected)
  };

  useEffect(() => {
    fetchCalls();
    fetchAiModels();
    fetchProviders();
    fetchPrompts();
    fetchAgents();
    fetchFieldMappings();
  }, [page, pageSize, filters]);

  // Load existing transcription settings
  const fetchTranscriptionSettings = async () => {
    try {
      const response = await fetch('/api/settings/transcription', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const settings = await response.json();
        setTranscriptionConfig(prev => ({
          ...prev,
          provider: settings.provider || 'openai',
          model: settings.model || 'whisper-1',
          language: settings.language || 'en'
        }));
      }
    } catch (error) {
      console.error('Error loading transcription settings:', error);
    }
  };

  useEffect(() => {
    fetchCalls();
    fetchAiModels();
    fetchProviders();
    fetchPrompts();
    fetchAgents();
    fetchFieldMappings();
    fetchTranscriptionSettings();
  }, [page, pageSize, filters]);

  // Auto-refresh processing calls every 10 seconds
  useEffect(() => {
    if (processingCalls.size > 0) {
      const interval = setInterval(() => {
        fetchCalls();
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [processingCalls]);

  // Handle audio playback for main table (only when NOT in dialog mode)
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !audioPlayer.audioUrl || audioPlayer.isDialogMode) return;

    // Set the audio source
    audio.src = audioPlayer.audioUrl;

    // Handle audio events
    const handleEnded = () => {
      handleAudioStop();
    };

    const handleError = (e) => {
      console.error('Audio playback error:', e);
      handleAudioError(e);
    };

    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    // Play or pause based on state
    if (audioPlayer.playing) {
      audio.play().catch(handleAudioError);
    } else {
      audio.pause();
    }

    return () => {
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, [audioPlayer.audioUrl, audioPlayer.playing, audioPlayer.isDialogMode]);

  // Cleanup audio on component unmount
  useEffect(() => {
    return () => {
      if (audioPlayer.audioUrl) {
        URL.revokeObjectURL(audioPlayer.audioUrl);
      }
    };
  }, [audioPlayer.audioUrl]);

  const fetchCalls = async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: pageSize,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
      };
      
      const response = await getCalls(params);
      setCalls(response.calls || []);
      setTotalCalls(response.total || 0);
      
      // Debug logging to see what data we're getting
      console.log('Calls data received:', response.calls?.length || 0, 'calls');
      if (response.calls && response.calls.length > 0) {
        console.log('First call data:', response.calls[0]);
      }
      
      // Update processing calls set
      const processing = new Set();
      response.calls?.forEach(call => {
        if (call.transcription_status === 'processing' || call.analysis_status === 'processing') {
          processing.add(call.id);
        }
      });
      setProcessingCalls(processing);
      
    } catch (err) {
      setError('Failed to fetch calls');
      showSnackbar('Failed to fetch calls', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchAiModels = async () => {
    try {
      const response = await getAIModels();
      setAiModels(response.data || []);
      
      // Also fetch the legacy providers for backward compatibility
      await fetchProviders();
    } catch (err) {
      console.error('Failed to fetch AI models:', err);
      // Fallback to legacy providers
      await fetchProviders();
    }
  };

  const fetchProviders = async () => {
    try {
      const response = await getTranscriptionProviders();
      setProviders(response.data || {});
    } catch (err) {
      console.error('Failed to fetch providers:', err);
    }
  };

  const fetchPrompts = async () => {
    try {
      const response = await getAIPrompts();
      setPrompts(response.data || []);
    } catch (err) {
      console.error('Error fetching prompts:', err);
    }
  };

  const fetchAgents = async () => {
    try {
      setAgentsLoading(true);
      const response = await fetch('/api/agents', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Agents API response:', data); // Debug log
      
      if (data.success) {
        setAgents(data.data || []);
        console.log('Loaded agents:', data.data?.length || 0); // Debug log
      } else {
        console.error('Agents API returned error:', data.error);
        setAgents([]);
      }
    } catch (err) {
      console.error('Error fetching agents:', err);
      showSnackbar('Failed to load agents', 'error');
      setAgents([]);
    } finally {
      setAgentsLoading(false);
    }
  };

  const fetchFieldMappings = async () => {
    try {
      const response = await getFieldMappings();
      console.log('Field mappings response:', response);
      
      // Convert array to object for easy lookup by field name
      const mappingsObj = {};
      if (response.success && response.data && Array.isArray(response.data)) {
        response.data.forEach(mapping => {
          mappingsObj[mapping.original_field] = mapping.custom_label || mapping.original_field;
        });
      }
      
      console.log('Processed field mappings:', mappingsObj);
      setFieldMappings(mappingsObj);
    } catch (error) {
      console.error('Error fetching field mappings:', error);
      // Set default mappings if API fails - include all fields that should have custom labels
      setFieldMappings({
        // Address fields that are currently working as Vehicle information
        address1: 'Vehicle Make',
        address2: 'Vehicle Model', 
        address3: 'Year of Manufacture',
        city: 'Current Insurer',
        postal_code: 'Postal Code',
        county: 'Current Excess',
        // Security phrase should map to ID Number
        security_phrase: 'ID Number',
        // Other common mappings
        vendor_lead_code: 'Current Premium'
      });
    }
  };

  // Helper function to get field label from mappings
  const getFieldLabel = (fieldName) => {
    return fieldMappings[fieldName] || fieldName.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleStartTranscription = async (call) => {
    try {
      setProcessingCalls(prev => new Set([...prev, call.id]));
      await startTranscription(call.id, transcriptionConfig);
      showSnackbar(`Transcription started for call ${call.id}`, 'success');
      setTranscriptionDialog({ open: false, call: null });
      fetchCalls();
    } catch (err) {
      setProcessingCalls(prev => {
        const newSet = new Set(prev);
        newSet.delete(call.id);
        return newSet;
      });
      showSnackbar(`Failed to start transcription: ${err.response?.data?.error || err.message}`, 'error');
    }
  };

  const handleStartAnalysis = async (call) => {
    try {
      setProcessingCalls(prev => new Set([...prev, call.id]));
      await startAnalysis(call.id, analysisConfig);
      showSnackbar(`Analysis started for call ${call.id}`, 'success');
      setAnalysisDialog({ open: false, call: null });
      fetchCalls();
    } catch (err) {
      setProcessingCalls(prev => {
        const newSet = new Set(prev);
        newSet.delete(call.id);
        return newSet;
      });
      showSnackbar(`Failed to start analysis: ${err.response?.data?.error || err.message}`, 'error');
    }
  };

  const handleBulkProcess = async () => {
    if (selectedCalls.length === 0) {
      showSnackbar('Please select calls to process', 'warning');
      return;
    }

    try {
      const response = await bulkProcessCalls(selectedCalls, transcriptionConfig);
      showSnackbar(`Bulk processing started for ${response.data.started} calls`, 'success');
      setBulkProcessDialog({ open: false });
      setSelectedCalls([]);
      fetchCalls();
    } catch (err) {
      showSnackbar(`Failed to start bulk processing: ${err.response?.data?.error || err.message}`, 'error');
    }
  };

  const handlePlayRecording = async (call) => {
    // Prevent double-clicking by checking if already loading
    if (audioPlayer.loading) return;

    try {
      // If same call is playing, toggle pause/play
      if (audioPlayer.callId === call.id && audioPlayer.audioUrl) {
        setAudioPlayer(prev => ({ ...prev, playing: !prev.playing }));
        return;
      }

      // Stop any currently playing audio and cleanup
      if (audioPlayer.audioUrl) {
        URL.revokeObjectURL(audioPlayer.audioUrl);
      }

      // Set loading state
      setAudioPlayer({ 
        loading: true, 
        playing: false, 
        callId: call.id, 
        audioUrl: null 
      });

      // Fetch audio blob
      const audioBlob = await getCallRecording(call.id);
      const audioUrl = URL.createObjectURL(audioBlob);
      
      // Set ready to play
      setAudioPlayer({ 
        loading: false, 
        playing: true, 
        callId: call.id, 
        audioUrl 
      });
      
    } catch (err) {
      setAudioPlayer({ 
        loading: false, 
        playing: false, 
        callId: null, 
        audioUrl: null 
      });
      showSnackbar(`Failed to play recording: ${err.response?.data?.error || err.message}`, 'error');
    }
  };

  const handleAudioPlay = () => {
    setAudioPlayer(prev => ({ ...prev, playing: true }));
  };

  const handleAudioPause = () => {
    setAudioPlayer(prev => ({ ...prev, playing: false }));
  };

  const handleAudioStop = () => {
    if (audioPlayer.audioUrl) {
      URL.revokeObjectURL(audioPlayer.audioUrl);
    }
    setAudioPlayer({ 
      loading: false, 
      playing: false, 
      callId: null, 
      audioUrl: null,
      isDialogMode: false 
    });
  };

  const handleAudioError = (error) => {
    console.error('Audio playback error:', error);
    handleAudioStop();
    showSnackbar('Error playing audio recording', 'error');
  };

  // Lead flow state
  const [leadFlow, setLeadFlow] = useState({
    data: null,
    loading: false,
    error: null
  });

  const fetchLeadInfo = async (callId) => {
    try {
      console.log('Fetching lead info for call ID:', callId);
      setLeadInfo(prev => ({ ...prev, data: null, loading: true, error: null }));
      const leadData = await getLeadsByCallId(callId);
      console.log('Lead data received:', leadData);
      // Extract the actual data array from the API response
      const actualLeadData = leadData.success ? leadData.data : [];
      setLeadInfo(prev => ({ ...prev, data: actualLeadData, loading: false, error: null }));
    } catch (error) {
      console.error('Error fetching lead info:', error);
      setLeadInfo(prev => ({ ...prev, data: null, loading: false, error: error.message }));
    }
  };

  // Fetch lead flow history
  const fetchLeadFlow = async (callId) => {
    try {
      console.log('Fetching lead flow for call ID:', callId);
      setLeadFlow(prev => ({ ...prev, data: null, loading: true, error: null }));
      const flowData = await getCallLeadFlow(callId);
      console.log('Lead flow data received:', flowData);
      const actualFlowData = flowData.success ? flowData.data : [];
      setLeadFlow(prev => ({ ...prev, data: actualFlowData, loading: false, error: null }));
    } catch (error) {
      console.error('Error fetching lead flow:', error);
      setLeadFlow(prev => ({ ...prev, data: null, loading: false, error: error.message }));
    }
  };

  const handleEditLead = (lead) => {
    setLeadInfo(prev => ({
      ...prev,
      editing: true,
      editData: { ...lead }
    }));
  };

  const handleCancelEditLead = () => {
    setLeadInfo(prev => ({
      ...prev,
      editing: false,
      editData: null
    }));
  };

  const handleSaveLead = async () => {
    try {
      if (!leadInfo.editData || !leadInfo.editData.id) {
        showSnackbar('No lead data to save', 'error');
        return;
      }

      console.log('Saving lead:', leadInfo.editData);
      
      // Use the updateLead function from qaService
      const response = await updateLead(leadInfo.editData.id, leadInfo.editData);
      
      if (response.success) {
        showSnackbar('Lead information updated successfully!', 'success');
        
        // Update the lead data in state with the saved changes
        setLeadInfo(prev => ({
          ...prev,
          data: prev.data.map(lead => 
            lead.id === leadInfo.editData.id ? { ...lead, ...leadInfo.editData } : lead
          ),
          editing: false,
          editData: null
        }));
      } else {
        showSnackbar('Failed to update lead information', 'error');
      }
    } catch (error) {
      console.error('Error saving lead:', error);
      showSnackbar(`Error saving lead information: ${error.message}`, 'error');
    }
  };

  const handleViewCallDetail = async (call) => {
    try {
      // Use the call data from main table for basic fields (works correctly)
      console.log('Using call data from main table:', call);
      
      // But also fetch QA evaluation data if it exists
      let callWithQA = { ...call };
      try {
        const qaResponse = await getCallForAnalysis(call.id);
        if (qaResponse.data.call?.qa_evaluation) {
          callWithQA.qa_evaluation = qaResponse.data.call.qa_evaluation;
          console.log('Added QA evaluation data:', qaResponse.data.call.qa_evaluation);
        }
      } catch (qaErr) {
        console.log('No QA evaluation data available:', qaErr.message);
        // This is fine - not all calls have QA evaluations
      }

      // Fetch audit trail if QA score exists
      if (call.qa_score || callWithQA.qa_evaluation) {
        try {
          const auditResponse = await getQAScoreAuditTrail(call.id);
          callWithQA.audit_trail = auditResponse.data.audit_trail;
          console.log('Added audit trail data:', callWithQA.audit_trail);
        } catch (auditErr) {
          console.log('No audit trail available:', auditErr.message);
          // This is fine - might be an older QA score without audit trail
        }
      }

      // If this call has a recording, ensure audio is loaded for the dialog
      if (call.recording_path || call.recording_url) {
        // If no audio currently loaded OR different call, load the audio
        if (!audioPlayer.audioUrl || audioPlayer.callId !== call.id) {
          try {
            const audioBlob = await getCallRecording(call.id);
            const audioUrl = URL.createObjectURL(audioBlob);
            setAudioPlayer({ 
              loading: false, 
              playing: false, 
              callId: call.id, 
              audioUrl,
              isDialogMode: true  // Switch to dialog mode
            });
          } catch (audioErr) {
            console.log('Could not preload audio for dialog:', audioErr.message);
            // This is fine - dialog will work without audio
          }
        } else {
          // Same call, just switch to dialog mode
          setAudioPlayer(prev => ({ ...prev, isDialogMode: true }));
        }
      }
      
      setCallDetailDialog({ open: true, call: callWithQA });
      
      // Fetch lead information
      fetchLeadInfo(call.id);
      
      // Fetch lead flow history
      fetchLeadFlow(call.id);
    } catch (err) {
      showSnackbar(`Failed to fetch call details: ${err.response?.data?.error || err.message}`, 'error');
    }
  };

  // Function to move lead to QA
  const handleMoveToQA = async (callId) => {
    try {
      const response = await moveLeadToQA(callId);
      if (response.success) {
        showSnackbar('Lead moved to QA successfully', 'success');
        // Refresh the lead flow data
        fetchLeadFlow(callId);
        // Refresh calls data
        fetchCalls();
      } else {
        showSnackbar('Failed to move lead to QA', 'error');
      }
    } catch (error) {
      console.error('Error moving lead to QA:', error);
      showSnackbar('Error moving lead to QA: ' + error.message, 'error');
    }
  };

  const handleQASubmit = async () => {
    try {
      setLoading(true);
      
      // Get the most recent QA score for this call
      const callWithQA = await getCallForAnalysis(qaDialog.call.id);
      const qaScoreData = callWithQA?.qa_score_data || {};
      
      // Create new submission data
      const submissionData = {
        call_id: qaDialog.call.id,
        
        // Handle individual QA scores
        qa_score: qaScore,
        comments: qaComments,
        
        // Store metadata
        metadata: {
          call_unique_id: qaDialog.call.unique_id,
          agent_id: qaDialog.call.agent_id,
          phone_number: qaDialog.call.phone || qaDialog.call.caller_id,
          submission_date: new Date().toISOString(),
          previous_score: qaScoreData.score || null,
          previous_comments: qaScoreData.comments || null
        }
      };
      
      await submitQAEvaluation(qaDialog.call.id, {
        qa_score: qaScore,
        comments: qaComments,
        metadata: submissionData.metadata
      });
      
      showSnackbar('QA evaluation submitted successfully!', 'success');
      setQADialog({ open: false, call: null });
      setQAScore('');
      setQAComments('');
      
      // Refresh calls data
      fetchCalls();
    } catch (error) {
      console.error('Error submitting QA evaluation:', error);
      showSnackbar('Error submitting QA evaluation: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Update recording path for a call
  const handleUpdateRecordingPath = async (call) => {
    try {
      setProcessingCalls(prev => new Set([...prev, call.id]));
      showSnackbar(`Searching for recording for call ${call.id}...`, 'info');
      
      const response = await updateRecordingPath(call.id);
      
      if (response.success) {
        showSnackbar(`Recording found and updated for call ${call.id}!`, 'success');
        // Refresh calls data to show updated recording path
        fetchCalls();
      } else {
        showSnackbar(`No recording found for call ${call.id}`, 'warning');
      }
    } catch (error) {
      console.error('Error updating recording path:', error);
      if (error.response && error.response.data && error.response.data.error) {
        showSnackbar(`Error: ${error.response.data.error}`, 'error');
      } else {
        showSnackbar('Error updating recording path', 'error');
      }
    } finally {
      setProcessingCalls(prev => {
        const newSet = new Set(prev);
        newSet.delete(call.id);
        return newSet;
      });
    }
  };

  // Save transcription settings to tenant configuration
  const handleSaveTranscriptionSettings = async () => {
    try {
      const settingsData = {
        provider: transcriptionConfig.provider,
        model: transcriptionConfig.model,
        language: transcriptionConfig.language || 'en'
      };

      const response = await fetch('/api/settings/transcription', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(settingsData)
      });

      if (response.ok) {
        showSnackbar('Transcription settings saved successfully!', 'success');
        setSettingsDialog({ open: false });
      } else {
        const errorData = await response.json();
        showSnackbar(`Error saving settings: ${errorData.error}`, 'error');
      }
    } catch (error) {
      console.error('Error saving transcription settings:', error);
      showSnackbar('Error saving transcription settings', 'error');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircleIcon color="success" />;
      case 'submitted': return <SendIcon color="primary" />;
      case 'processing': return <CircularProgress size={16} />;
      case 'failed': return <ErrorIcon color="error" />;
      case 'pending': return <PendingIcon color="warning" />;
      default: return <PendingIcon />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'submitted': return 'primary';
      case 'processing': return 'info';
      case 'failed': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 90 },
    {
      field: 'agent',
      headerName: 'Agent',
      width: 180,
      renderCell: (params) => {
        // Get agent information from the agent relation or metadata
        let agentDisplay = 'N/A';
        
        // First try the agent relation (if populated by backend)
        if (params.row.agent) {
          if (params.row.agent.name) {
            agentDisplay = params.row.agent.name;
          } else if (params.row.agent.vici_id) {
            agentDisplay = params.row.agent.vici_id;
          } else if (params.row.agent.username) {
            agentDisplay = params.row.agent.username;
          }
        } 
        // Then try metadata
        else if (params.row.metadata) {
          const metadata = params.row.metadata;
          if (metadata.agent_username && metadata.agent_username !== 'agent001') {
            agentDisplay = metadata.agent_username;
          } else if (metadata.agent_name && metadata.agent_name !== 'agent001') {
            agentDisplay = metadata.agent_name;
          } else if (metadata.agent_id && metadata.agent_id !== 'agent001') {
            agentDisplay = metadata.agent_id;
          }
        }
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PhoneIcon sx={{ mr: 1, fontSize: 16 }} />
            <Typography variant="body2">
              {agentDisplay}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'customer',
      headerName: 'Customer',
      width: 180,
      renderCell: (params) => {
        // Extract customer name from metadata (for calls created from leads)
        const metadata = params.row.metadata || {};
        const customerName = metadata.fullname || 
                           (metadata.phone_number ? `Customer (${metadata.phone_number})` : '') ||
                           (params.row.phone ? `Customer (${params.row.phone})` : '') ||
                           'N/A';
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2">
              {customerName}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'phone_number',
      headerName: 'Phone Number',
      width: 140,
      renderCell: (params) => {
        // Extract phone number from metadata or direct field
        const metadata = params.row.metadata || {};
        const phoneNumber = metadata.phone_number || 
                           params.row.phone || 
                           params.row.caller_id ||
                           'N/A';
        
        return (
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {phoneNumber}
          </Typography>
        );
      },
    },
    {
      field: 'duration',
      headerName: 'Duration',
      width: 100,
      renderCell: (params) => {
        if (!params.value || params.value === 0) {
          return 'N/A';
        }
        const minutes = Math.floor(params.value / 60);
        const seconds = params.value % 60;
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
      },
    },
    {
      field: 'status',
      headerName: 'Call Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getStatusColor(params.value)}
          size="small"
        />
      ),
    },
    {
      field: 'transcription_status',
      headerName: 'Transcription',
      width: 140,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getStatusIcon(params.value)}
          <Chip
            label={params.value || 'pending'}
            color={getStatusColor(params.value)}
            size="small"
          />
        </Box>
      ),
    },
    {
      field: 'analysis_status',
      headerName: 'Analysis',
      width: 120,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getStatusIcon(params.value)}
          <Chip
            label={params.value || 'pending'}
            color={getStatusColor(params.value)}
            size="small"
          />
        </Box>
      ),
    },
    {
      field: 'qa_score',
      headerName: 'QA Score',
      width: 100,
      renderCell: (params) => {
        // Debug logging to see what qa_score values we're getting
        console.log('QA Score cell - params.value:', params.value, 'type:', typeof params.value);
        
        // Handle both old numerical scores and new categorical scores
        let displayValue = 'N/A';
        let chipStyle = { backgroundColor: '#f5f5f5', color: '#666' }; // Default grey
        
        if (params.value) {
          // If it's a string
          if (typeof params.value === 'string') {
            // Check if it's a numeric string first
            const numericValue = parseFloat(params.value);
            if (!isNaN(numericValue)) {
              // Handle numeric strings (convert to categorical)
              if (numericValue >= 80) {
                displayValue = 'Good';
                chipStyle = { backgroundColor: '#2196F3', color: 'white' }; // Blue
              } else if (numericValue >= 60) {
                displayValue = 'Average';
                chipStyle = { backgroundColor: '#FFD700', color: 'black' }; // Yellow
              } else {
                displayValue = 'Poor';
                chipStyle = { backgroundColor: '#9E9E9E', color: 'white' }; // Grey
              }
            } else {
              // Handle categorical strings
              displayValue = params.value;
              switch (params.value.toLowerCase()) {
                case 'excellent':
                  displayValue = 'Excellent';
                  chipStyle = { backgroundColor: '#4CAF50', color: 'white' }; // Green
                  break;
                case 'good':
                  chipStyle = { backgroundColor: '#2196F3', color: 'white' }; // Blue
                  break;
                case 'average':
                  chipStyle = { backgroundColor: '#FFD700', color: 'black' }; // Yellow
                  break;
                case 'poor':
                  chipStyle = { backgroundColor: '#9E9E9E', color: 'white' }; // Grey
                  break;
                case 'rejected':
                  chipStyle = { backgroundColor: '#F44336', color: 'white' }; // Red
                  break;
              }
            }
          } 
          // If it's a number (old numerical system - convert to categorical)
          else if (typeof params.value === 'number') {
            if (params.value >= 80) {
              displayValue = 'Good';
              chipStyle = { backgroundColor: '#2196F3', color: 'white' }; // Blue
            } else if (params.value >= 60) {
              displayValue = 'Average';
              chipStyle = { backgroundColor: '#FFD700', color: 'black' }; // Yellow
            } else {
              displayValue = 'Poor';
              chipStyle = { backgroundColor: '#9E9E9E', color: 'white' }; // Grey
            }
          }
        }
        
        return (
          <Box>
            <Chip
              label={displayValue}
              size="small"
              sx={{ 
                fontWeight: 'bold',
                ...chipStyle
              }}
            />
          </Box>
        );
      },
    },
    {
      field: 'createdAt',
      headerName: 'Date',
      width: 160,
      renderCell: (params) => {
        if (!params.value) return 'N/A';
        try {
          const date = new Date(params.value);
          return isNaN(date.getTime()) ? 'Invalid Date' : format(date, 'MMM d, yyyy HH:mm');
        } catch (error) {
          return 'Invalid Date';
        }
      },
    },
    {
      field: 'lead_status',
      headerName: 'Lead Status',
      width: 160,
      minWidth: 130,
      maxWidth: 220,
      sortable: true,
      filterable: true,
      hideable: false,
      renderCell: (params) => {
        // Check if call has an associated lead (plural: Leads)
        const leads = params.row.Leads;
        const hasLead = leads && leads.length > 0;
        
        // Check for lead flow information
        const hasLeadFlow = params.row.lead_sent_to_qa_at || 
                           params.row.lead_sent_to_api_at || 
                           params.row.lead_api_response_received_at || 
                           params.row.lead_report_uploaded_at;
        
        // Show flow status if available
        if (hasLeadFlow) {
          if (params.row.lead_report_uploaded_at) {
            return "Reported";
          } else if (params.row.lead_api_response_received_at) {
            return "API Resp";
          } else if (params.row.lead_sent_to_api_at) {
            return "Sent API";
          } else if (params.row.lead_sent_to_qa_at) {
            return "In QA";
          }
        }
        
        // Otherwise show if lead exists
        if (hasLead) {
          // Get disposition from first lead if available
          const disposition = leads[0].disposition || params.row.status;
          return disposition || "Lead";
        }
        
        return "-";
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="View Details">
            <IconButton size="small" onClick={() => handleViewCallDetail(params.row)}>
              <ReviewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          {(params.row.recording_path || params.row.recording_url) && (
            <Tooltip title={
              audioPlayer.loading && audioPlayer.callId === params.row.id ? "Loading..." :
              audioPlayer.playing && audioPlayer.callId === params.row.id ? "Pause Recording" : 
              "Play Recording"
            }>
              <IconButton 
                size="small" 
                onClick={() => handlePlayRecording(params.row)}
                disabled={audioPlayer.loading && audioPlayer.callId === params.row.id}
                color={audioPlayer.playing && audioPlayer.callId === params.row.id ? "secondary" : "default"}
              >
                {audioPlayer.loading && audioPlayer.callId === params.row.id ? (
                  <CircularProgress size={16} />
                ) : audioPlayer.playing && audioPlayer.callId === params.row.id ? (
                  <PauseIcon fontSize="small" />
                ) : (
                  <PlayCircleIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          )}

          {canTranscribe() && (
            <Tooltip title={params.row.transcription_status === 'completed' ? "Re-transcribe with different language" : "Start Transcription"}>
              <IconButton 
                size="small" 
                onClick={() => setTranscriptionDialog({ open: true, call: params.row })}
                disabled={params.row.transcription_status === 'processing'}
                color={params.row.transcription_status === 'completed' ? "secondary" : "default"}
              >
                <TranscribeIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {canAnalyze() && (
            <Tooltip title="Start Analysis">
              <IconButton 
                size="small" 
                onClick={() => setAnalysisDialog({ open: true, call: params.row })}
                disabled={params.row.transcription_status !== 'completed' || params.row.analysis_status === 'processing' || params.row.analysis_status === 'completed'}
              >
                <AnalyzeIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {canUpdateQA() && (
            <Tooltip title="QA Score">
              <IconButton 
                size="small" 
                onClick={() => setQADialog({ open: true, call: params.row })}
              >
                <StarIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {/* Update Recording Path button - only show when no recording exists */}
          {!params.row.recording_path && !params.row.recording_url && canTranscribe() && (
            <Tooltip title="Find Recording">
              <IconButton 
                size="small" 
                onClick={() => handleUpdateRecordingPath(params.row)}
                disabled={processingCalls.has(params.row.id)}
                color="primary"
              >
                {processingCalls.has(params.row.id) ? (
                  <CircularProgress size={16} />
                ) : (
                  <FindRecordingIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  const handleLeadSubmissionComplete = (results) => {
    const successCount = results.filter(r => r.success).length;
    const failureCount = results.filter(r => !r.success).length;
    showSnackbar(`Lead submission completed: ${successCount} successful, ${failureCount} failed`, 'success');
    
    // Optionally refresh calls to show updated status
    fetchCalls();
  };

  const getSelectedCallsData = () => {
    return calls.filter(call => selectedCalls.includes(call.id));
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Hidden audio element for main table playback */}
      <audio ref={audioRef} style={{ display: 'none' }} />
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          📞 Call Analysis & Transcription
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => setSettingsDialog({ open: true })}
          >
            Settings
          </Button>
          {canBulkProcess() && (
            <Button
              variant="contained"
              startIcon={<BulkIcon />}
              onClick={() => setBulkProcessDialog({ open: true })}
              disabled={selectedCalls.length === 0}
            >
              Bulk Process ({selectedCalls.length})
            </Button>
          )}
          <Button
            variant="contained"
            color="secondary"
            startIcon={<SendIcon />}
            onClick={() => setLeadSubmissionDialog({ open: true })}
            disabled={selectedCalls.length === 0}
          >
            Submit Leads ({selectedCalls.length})
          </Button>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={fetchCalls}
          >
            Refresh
          </Button>
        </Stack>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Filters</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Search"
                variant="outlined"
                size="small"
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Call Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Call Status"
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="submitted">Submitted</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                  <MenuItem value="in-progress">In Progress</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Transcription</InputLabel>
                <Select
                  value={filters.transcription_status}
                  label="Transcription"
                  onChange={(e) => setFilters(prev => ({ ...prev, transcription_status: e.target.value }))}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="processing">Processing</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Analysis</InputLabel>
                <Select
                  value={filters.analysis_status}
                  label="Analysis"
                  onChange={(e) => setFilters(prev => ({ ...prev, analysis_status: e.target.value }))}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="processing">Processing</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Agent</InputLabel>
                <Select
                  value={filters.agent_id}
                  label="Agent"
                  onChange={(e) => setFilters(prev => ({ ...prev, agent_id: e.target.value }))}
                  disabled={agentsLoading}
                >
                  <MenuItem value="">All Agents</MenuItem>
                  {agents.map(agent => (
                    <MenuItem key={agent.id} value={agent.id}>
                      {agent.name} ({agent.vici_id || agent.username || agent.id})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                label="Start Date"
                variant="outlined"
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={filters.start_date}
                onChange={(e) => setFilters(prev => ({ ...prev, start_date: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                label="End Date"
                variant="outlined"
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={filters.end_date}
                onChange={(e) => setFilters(prev => ({ ...prev, end_date: e.target.value }))}
              />
            </Grid>
            {/* Lead Status Filter */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Lead Status</InputLabel>
                <Select
                  value={filters.lead_status || ''}
                  label="Lead Status"
                  onChange={(e) => setFilters(prev => ({ ...prev, lead_status: e.target.value }))}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="no-lead">No Lead</MenuItem>
                  <MenuItem value="in-qa">In QA</MenuItem>
                  <MenuItem value="sent-api">Sent API</MenuItem>
                  <MenuItem value="api-resp">API Response</MenuItem>
                  <MenuItem value="reported">Reported</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Main Data Grid */}
      <Paper sx={{ height: 600, width: '100%' }}>
        {/* Debug information - remove this in production */}
        <Box sx={{ p: 1, backgroundColor: '#e3f2fd', fontSize: '0.8rem' }}>
          Columns: {columns.map(col => col.field).join(', ')}
        </Box>
        <DataGrid
          rows={calls}
          columns={columns}
          paginationModel={{ page, pageSize }}
          rowCount={totalCalls}
          pageSizeOptions={[10, 25, 50, 100]}
          checkboxSelection
          disableRowSelectionOnClick
          loading={loading}
          paginationMode="server"
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
          onRowSelectionModelChange={(newSelection) => setSelectedCalls(newSelection)}
          rowSelectionModel={selectedCalls}
          sx={{
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={(newModel) => {
            // Prevent lead_status from being hidden
            if (!newModel.lead_status) {
              console.log('Preventing lead_status column from being hidden');
              setColumnVisibilityModel({ ...newModel, lead_status: true });
            } else {
              setColumnVisibilityModel(newModel);
            }
          }}
        />
      </Paper>

      {/* Transcription Dialog */}
      {transcriptionDialog.open && transcriptionDialog.call && (
        <Dialog open={true} onClose={() => setTranscriptionDialog({ open: false, call: null })} maxWidth="sm" fullWidth>
          <DialogTitle>
            {transcriptionDialog.call.transcription_status === 'completed' ? 'Re-transcribe Call' : 'Start Transcription'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              {transcriptionDialog.call.transcription_status === 'completed' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  This call was already transcribed. You can re-transcribe it with a different language or model if needed.
                </Alert>
              )}
              <Alert severity="info" sx={{ mb: 2 }}>
                💡 For Zulu language calls, go to Settings → Transcription Language → "Zulu (isiZulu)" first, then transcribe.
              </Alert>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Provider</InputLabel>
                <Select
                  value={transcriptionConfig.provider || 'openai'}
                  label="Provider"
                  onChange={(e) => setTranscriptionConfig({ ...transcriptionConfig, provider: e.target.value })}
                >
                  {providers && Object.keys(providers).length > 0 ? (
                    Object.entries(providers).map(([providerId, providerData]) => (
                      <MenuItem key={providerId} value={providerId}>
                        {typeof providerData === 'string' ? providerData : (providerData?.name || providerId)}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="openai">OpenAI</MenuItem>
                  )}
                </Select>
              </FormControl>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Model</InputLabel>
                <Select
                  value={transcriptionConfig.model || 'whisper-1'}
                  label="Model"
                  onChange={(e) => setTranscriptionConfig({ ...transcriptionConfig, model: e.target.value })}
                >
                  {aiModels && aiModels.length > 0 ? (
                    aiModels
                      .filter(model => model.is_active === true && 
                        (model.capabilities?.transcription === true || 
                         model.model_id?.toLowerCase().includes('whisper')))
                      .map(model => (
                        <MenuItem key={model.id} value={model.model_id || model.name}>
                          {model.name} ({model.description || model.model_id})
                        </MenuItem>
                      ))
                  ) : (
                    <MenuItem value="whisper-1">Whisper-1</MenuItem>
                  )}
                  {/* Add fallback option if no models found */}
                  {aiModels && aiModels.filter(model => model.is_active === true && 
                    (model.capabilities?.transcription === true || 
                     model.model_id?.toLowerCase().includes('whisper'))).length === 0 && (
                    <MenuItem value="whisper-1">Whisper-1 (default)</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTranscriptionDialog({ open: false, call: null })}>
              Cancel
            </Button>
            <Button
              onClick={() => handleStartTranscription(transcriptionDialog.call)}
              disabled={processingCalls.has(transcriptionDialog.call.id)}
              variant="contained"
              color="primary"
            >
              {processingCalls.has(transcriptionDialog.call.id) ? 'Processing...' : 'Start Transcription'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Analysis Dialog */}
      <Dialog open={analysisDialog.open} onClose={() => setAnalysisDialog({ open: false, call: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Start AI Analysis</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>AI Model</InputLabel>
              <Select
                value={analysisConfig.model_id || ''}
                label="AI Model"
                onChange={(e) => {
                  const selectedModel = aiModels.find(model => model.id === e.target.value);
                  setAnalysisConfig(prev => ({ 
                    ...prev, 
                    model_id: e.target.value,
                    provider: selectedModel?.provider?.toLowerCase() || 'openai',
                    model: selectedModel?.model_id || 'gpt-4'
                  }));
                }}
              >
                {aiModels
                  .filter(model => model.is_active === true && 
                    (model.capabilities?.analysis === true || 
                     model.model_id?.includes('gpt') || 
                     model.provider?.toLowerCase() === 'openai'))
                  .map(model => (
                    <MenuItem key={model.id} value={model.id}>
                      {model.name} ({model.provider} - {model.model_id})
                    </MenuItem>
                  ))
                }
                {/* Fallback message if no models available */}
                {aiModels.filter(model => model.is_active === true && 
                  (model.capabilities?.analysis === true || 
                   model.model_id?.includes('gpt') || 
                   model.provider?.toLowerCase() === 'openai')).length === 0 && (
                  <MenuItem value="" disabled>
                    No analysis models available. Please add models in AI Models menu.
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Analysis Prompt</InputLabel>
              <Select
                value={analysisConfig.prompt_id || ''}
                label="Analysis Prompt"
                onChange={(e) => setAnalysisConfig(prev => ({ ...prev, prompt_id: e.target.value }))}
              >
                <MenuItem value="">Default Analysis</MenuItem>
                {prompts.map(prompt => (
                  <MenuItem key={prompt.id} value={prompt.id}>{prompt.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnalysisDialog({ open: false, call: null })}>Cancel</Button>
          <Button 
            onClick={() => handleStartAnalysis(analysisDialog.call)} 
            variant="contained"
            disabled={!analysisDialog.call || !analysisConfig.model_id}
          >
            Start Analysis
          </Button>
        </DialogActions>
      </Dialog>

      {/* Call Detail Dialog */}
      <Dialog open={callDetailDialog.open} onClose={() => setCallDetailDialog({ open: false, call: null })} maxWidth="lg" fullWidth>
        <DialogTitle>Call Details</DialogTitle>
        <DialogContent>
          {callDetailDialog.call && (
            <Box sx={{ pt: 2 }}>
              {/* Audio Player - Show if recording exists */}
              {(callDetailDialog.call.recording_path || callDetailDialog.call.recording_url) && (
                <Box sx={{ mb: 3 }}>
                  {audioPlayer.audioUrl && audioPlayer.callId === callDetailDialog.call.id ? (
                    <AudioPlayer
                      audioUrl={audioPlayer.audioUrl}
                      isPlaying={audioPlayer.playing}
                      callId={callDetailDialog.call?.id}
                      onPlay={handleAudioPlay}
                      onPause={handleAudioPause}
                      onStop={handleAudioStop}
                      onError={handleAudioError}
                    />
                  ) : (
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handlePlayRecording(callDetailDialog.call)}
                        startIcon={audioPlayer.loading && audioPlayer.callId === callDetailDialog.call.id ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                        disabled={audioPlayer.loading && audioPlayer.callId === callDetailDialog.call.id}
                      >
                        {audioPlayer.loading && audioPlayer.callId === callDetailDialog.call.id ? 'Loading Audio...' : 'Load & Play Recording'}
                      </Button>
                      <Typography variant="body2" color="text.secondary">
                        Click to load the recording
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Call Information</Typography>
                  <Typography><strong>ID:</strong> {callDetailDialog.call.unique_id || callDetailDialog.call.id}</Typography>
                  <Typography><strong>Agent:</strong> {(() => {
                    const call = callDetailDialog.call;
                    const metadata = call.metadata || {};
                    
                    // Priority order: metadata fields > agent relation > fallback
                    if (metadata.agent_username && metadata.agent_username !== 'agent001') {
                      return metadata.agent_username;
                    } else if (metadata.agent_id && metadata.agent_id !== 'agent001') {
                      return metadata.agent_id;
                    } else if (metadata.agent_name && metadata.agent_name !== 'agent001') {
                      return metadata.agent_name;
                    } else if (metadata.agent_user && metadata.agent_user !== 'agent001') {
                      return metadata.agent_user;
                    } else if (call.agent?.name && call.agent.name !== 'agent001' && !call.agent.name.includes('System Agent')) {
                      return call.agent.name;
                    } else if (call.agent?.username && call.agent.username !== 'agent001') {
                      return call.agent.username;
                    } else {
                      return 'System Agent';
                    }
                  })()}</Typography>
                  <Typography><strong>Duration:</strong> {
                    callDetailDialog.call.duration > 0 
                      ? `${Math.floor(callDetailDialog.call.duration / 60)}:${(callDetailDialog.call.duration % 60).toString().padStart(2, '0')}`
                      : 'N/A'
                  }</Typography>
                  <Typography><strong>Status:</strong> {callDetailDialog.call.status || 'Processing'}</Typography>
                  <Typography><strong>Date:</strong> {
                    (() => {
                      // Try both field names to be safe
                      const dateField = callDetailDialog.call.createdAt || callDetailDialog.call.created_at;
                      if (!dateField) return 'N/A';
                      try {
                        const date = new Date(dateField);
                        return isNaN(date.getTime()) ? 'Invalid Date' : format(date, 'MMM d, yyyy HH:mm');
                      } catch (error) {
                        return 'Invalid Date';
                      }
                    })()
                  }</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Processing Status</Typography>
                  <Typography><strong>Transcription:</strong> {callDetailDialog.call.transcription_status || callDetailDialog.call.transcriptionStatus || 'Not Started'}</Typography>
                  <Typography><strong>Analysis:</strong> {callDetailDialog.call.analysis_status || callDetailDialog.call.analysisStatus || 'Not Started'}</Typography>
                  {callDetailDialog.call.transcription_provider && (
                    <Typography><strong>Provider:</strong> {callDetailDialog.call.transcription_provider}</Typography>
                  )}
                </Grid>
              </Grid>

              {callDetailDialog.call.transcription && (
                <Accordion sx={{ mt: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">Transcription</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', backgroundColor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                      {callDetailDialog.call.transcription}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              )}

              {callDetailDialog.call.ai_analysis && (
                <Accordion sx={{ mt: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">AI Analysis</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {typeof callDetailDialog.call.ai_analysis === 'string' 
                        ? callDetailDialog.call.ai_analysis 
                        : JSON.stringify(callDetailDialog.call.ai_analysis, null, 2)}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* QA Evaluation Section */}
              {callDetailDialog.call.qa_evaluation && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>QA Evaluation</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        label="Score"
                        variant="outlined"
                        size="small"
                        value={callDetailDialog.call.qa_evaluation.score || 'N/A'}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Comments"
                        variant="outlined"
                        size="small"
                        multiline
                        rows={3}
                        value={callDetailDialog.call.qa_evaluation.comments || 'N/A'}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* QA Score Audit Trail */}
              {callDetailDialog.call.audit_trail && callDetailDialog.call.audit_trail.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>QA Score Audit Trail</Typography>
                  <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                    {callDetailDialog.call.audit_trail.map((entry, index) => (
                      <Card key={index} sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5' }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2">
                              <strong>Previous Score:</strong> {entry.previous_score || 'N/A'}
                            </Typography>
                            <Typography variant="body2">
                              <strong>New Score:</strong> {entry.new_score || 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2">
                              <strong>Changed By:</strong> {entry.changedBy?.name || entry.changedBy?.email || entry.changed_by || 'N/A'}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Date:</strong> {entry.createdAt ? format(new Date(entry.createdAt), 'MMM d, yyyy HH:mm') : entry.created_at ? format(new Date(entry.created_at), 'MMM d, yyyy HH:mm') : 'N/A'}
                            </Typography>
                          </Grid>
                          {entry.reason && (
                            <Grid item xs={12}>
                              <Typography variant="body2">
                                <strong>Reason:</strong> {entry.reason}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Card>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Action Buttons */}
              <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {(!callDetailDialog.call.transcription || callDetailDialog.call.transcription_status === 'failed') && (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      const call = callDetailDialog.call;
                      setCallDetailDialog({ open: false, call: null });
                      setTimeout(() => {
                        setTranscriptionDialog({ open: true, call: call });
                      }, 100);
                    }}
                  >
                    {callDetailDialog.call.transcription_status === 'failed' ? 'Retry Transcription' : 'Start Transcription'}
                  </Button>
                )}
                
                {callDetailDialog.call.transcription && (!callDetailDialog.call.ai_analysis || callDetailDialog.call.analysis_status === 'failed') && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => {
                      const call = callDetailDialog.call;
                      setCallDetailDialog({ open: false, call: null });
                      setTimeout(() => {
                        setAnalysisDialog({ open: true, call: call });
                      }, 100);
                    }}
                  >
                    {callDetailDialog.call.analysis_status === 'failed' ? 'Retry Analysis' : 'Start Analysis'}
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCallDetailDialog({ open: false, call: null })}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Process Dialog */}
      <Dialog open={bulkProcessDialog.open} onClose={() => setBulkProcessDialog({ open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk Process Calls</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Process {selectedCalls.length} selected calls with the following configuration:
          </Typography>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Transcription Model</InputLabel>
              <Select
                value={transcriptionConfig.model_id || ''}
                label="Transcription Model"
                onChange={(e) => {
                  const selectedModel = aiModels.find(model => model.id === e.target.value);
                  setTranscriptionConfig(prev => ({ 
                    ...prev, 
                    model_id: e.target.value,
                    provider: selectedModel?.provider?.toLowerCase() || 'openai',
                    model: selectedModel?.model_id || 'whisper-1'
                  }));
                }}
              >
                {aiModels
                  .filter(model => model.is_active === true && 
                    (model.capabilities?.transcription === true || 
                     model.model_id?.includes('whisper') || 
                     model.provider?.toLowerCase() === 'openai'))
                  .map(model => (
                    <MenuItem key={model.id} value={model.id}>
                      {model.name} ({model.provider} - {model.model_id})
                    </MenuItem>
                  ))
                }
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkProcessDialog({ open: false })}>Cancel</Button>
          <Button onClick={handleBulkProcess} variant="contained" disabled={!transcriptionConfig.model_id}>
            Start Bulk Processing
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lead Submission Dialog */}
      {leadSubmissionDialog.open && (
        <LeadSubmission
          selectedCalls={getSelectedCallsData()}
          onClose={() => setLeadSubmissionDialog({ open: false })}
          onSubmissionComplete={handleLeadSubmissionComplete}
        />
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Settings Dialog */}
      <Dialog open={settingsDialog.open} onClose={() => setSettingsDialog({ open: false })} maxWidth="md" fullWidth>
        <DialogTitle>Call Processing Settings</DialogTitle>
        <DialogContent>
          <Typography variant="h6" gutterBottom>Default Transcription Settings</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Default Transcription Model</InputLabel>
                <Select
                  value={transcriptionConfig.model_id || ''}
                  label="Default Transcription Model"
                  onChange={(e) => {
                    const selectedModel = aiModels.find(model => model.id === e.target.value);
                    setTranscriptionConfig(prev => ({ 
                      ...prev, 
                      model_id: e.target.value,
                      provider: selectedModel?.provider?.toLowerCase() || 'openai',
                      model: selectedModel?.model_id || 'whisper-1'
                    }));
                  }}
                >
                  {aiModels
                    .filter(model => model.is_active === true && 
                      (model.capabilities?.transcription === true || 
                       model.model_id?.includes('whisper') || 
                       model.provider?.toLowerCase() === 'openai'))
                    .map(model => (
                      <MenuItem key={model.id} value={model.id}>
                        {model.name} ({model.provider} - {model.model_id})
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Transcription Language</InputLabel>
                <Select
                  value={transcriptionConfig.language || 'en'}
                  label="Transcription Language"
                  onChange={(e) => setTranscriptionConfig(prev => ({ 
                    ...prev, 
                    language: e.target.value 
                  }))}
                >
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="zu">Zulu (isiZulu)</MenuItem>
                  <MenuItem value="af">Afrikaans</MenuItem>
                  <MenuItem value="es">Spanish</MenuItem>
                  <MenuItem value="fr">French</MenuItem>
                  <MenuItem value="de">German</MenuItem>
                  <MenuItem value="pt">Portuguese</MenuItem>
                  <MenuItem value="it">Italian</MenuItem>
                  <MenuItem value="ar">Arabic</MenuItem>
                  <MenuItem value="zh">Chinese</MenuItem>
                  <MenuItem value="ja">Japanese</MenuItem>
                  <MenuItem value="ko">Korean</MenuItem>
                  <MenuItem value="hi">Hindi</MenuItem>
                  <MenuItem value="ru">Russian</MenuItem>
                  <MenuItem value="nl">Dutch</MenuItem>
                  <MenuItem value="sv">Swedish</MenuItem>
                  <MenuItem value="da">Danish</MenuItem>
                  <MenuItem value="no">Norwegian</MenuItem>
                  <MenuItem value="fi">Finnish</MenuItem>
                  <MenuItem value="pl">Polish</MenuItem>
                  <MenuItem value="tr">Turkish</MenuItem>
                  <MenuItem value="he">Hebrew</MenuItem>
                  <MenuItem value="auto">Auto-detect</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Typography variant="h6" gutterBottom>Default Analysis Settings</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Analysis Model</InputLabel>
                <Select
                  value={analysisConfig.model_id || ''}
                  label="Analysis Model"
                  onChange={(e) => {
                    const selectedModel = aiModels.find(model => model.id === e.target.value);
                    setAnalysisConfig(prev => ({ 
                      ...prev, 
                      model_id: e.target.value,
                      provider: selectedModel?.provider?.toLowerCase() || 'openai',
                      model: selectedModel?.model_id || 'gpt-4'
                    }));
                  }}
                >
                  {aiModels
                    .filter(model => model.is_active === true && 
                      (model.capabilities?.analysis === true || 
                       model.model_id?.includes('gpt') || 
                       model.provider?.toLowerCase() === 'openai'))
                    .map(model => (
                      <MenuItem key={model.id} value={model.id}>
                        {model.name} ({model.provider} - {model.model_id})
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Default Prompt</InputLabel>
                <Select
                  value={analysisConfig.prompt_id || ''}
                  label="Default Prompt"
                  onChange={(e) => setAnalysisConfig(prev => ({ ...prev, prompt_id: e.target.value }))}
                >
                  <MenuItem value="">Default Analysis</MenuItem>
                  {prompts.map(prompt => (
                    <MenuItem key={prompt.id} value={prompt.id}>{prompt.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsDialog({ open: false })}>Close</Button>
          <Button 
            onClick={() => handleSaveTranscriptionSettings()}
            variant="contained"
            color="primary"
          >
            Save Settings
          </Button>
        </DialogActions>
      </Dialog>

      {/* QA Scoring Dialog */}
      <Dialog open={qaDialog.open} onClose={() => setQADialog({ open: false, call: null })} maxWidth="md" fullWidth>
        <DialogTitle>QA Evaluation - Call {qaDialog.call?.unique_id}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {qaDialog.call && (
              <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="h6" gutterBottom>Call Information</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography><strong>Agent:</strong> {qaDialog.call.metadata?.agent_username || qaDialog.call.Agent?.name || 'Unknown'}</Typography>
                    <Typography><strong>Phone:</strong> {qaDialog.call.phone}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography><strong>Duration:</strong> {qaDialog.call.duration > 0 ? `${Math.floor(qaDialog.call.duration / 60)}:${(qaDialog.call.duration % 60).toString().padStart(2, '0')}` : 'N/A'}</Typography>
                    <Typography><strong>Campaign:</strong> {qaDialog.call.campaign || 'Unknown'}</Typography>
                  </Grid>
                </Grid>
              </Box>
            )}

            <Typography variant="h6" gutterBottom>QA Evaluation</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom><strong>Call Quality Rating</strong></Typography>
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>Select Rating</InputLabel>
                  <Select
                    value={qaScore}
                    label="Select Rating"
                    onChange={(e) => setQAScore(e.target.value)}
                  >
                    <MenuItem value="Good">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip 
                          label="Good" 
                          size="small" 
                          sx={{ backgroundColor: '#2196F3', color: 'white' }}
                        />
                        <Typography>- Excellent performance, meets all standards</Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="Average">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip 
                          label="Average" 
                          size="small" 
                          sx={{ backgroundColor: '#FFD700', color: 'black' }}
                        />
                        <Typography>- Acceptable performance, some areas for improvement</Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="Poor">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip 
                          label="Poor" 
                          size="small" 
                          sx={{ backgroundColor: '#9E9E9E', color: 'white' }}
                        />
                        <Typography>- Below average performance, needs improvement</Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="Rejected">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip 
                          label="Rejected" 
                          size="small" 
                          sx={{ backgroundColor: '#F44336', color: 'white' }}
                        />
                        <Typography>- Poor performance, significant issues identified</Typography>
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Comments"
                value={qaComments}
                onChange={(e) => setQAComments(e.target.value)}
                placeholder="Additional comments about the call quality..."
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQADialog({ open: false, call: null })}>Cancel</Button>
          <Button 
            onClick={handleQASubmit} 
            variant="contained"
            disabled={!qaScore}
          >
            Submit QA Evaluation
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Calls;